import { Accordion, AccordionItem, Chip } from '@heroui/react';
import type { ReactNode } from 'react';

import { LogCard, nestedAccordionItemClasses } from './shared';
import type { LogCardAccordionItemClasses } from './shared/accordionStyles';
import { useGuardrailStyles } from '../hooks/useGuardrailStyles';
import { GuardrailConfig, formatGuardrailType } from '../utils/logParser';

import i18n from '~/common/helpers/i18n';

interface GuardrailCardProps {
  config: GuardrailConfig;
  annotationSlot?: ReactNode;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  itemKey?: string;
  itemClasses?: LogCardAccordionItemClasses;
  className?: string;
  nestedAccordionSyncKey?: number;
  nestedAccordionSyncExpanded?: boolean;
}

/**
 * Component to display guardrail logs in a structured card with status badges and expandable details
 */
export function GuardrailCard({
  config,
  annotationSlot,
  isExpanded,
  onExpandedChange,
  itemKey,
  itemClasses,
  className,
  nestedAccordionSyncKey,
  nestedAccordionSyncExpanded
}: GuardrailCardProps) {
  const isSuccess = config.status === 'success';

  const { hasDetails, detailsBgColor, detailsTextColor } = useGuardrailStyles({
    isSuccess,
    message: config.message
  });

  const cardTitle =
    config.type === 'input'
      ? (i18n.t('scenes.build.execution.logs.guardrail.input.label') as string)
      : (i18n.t(
          'scenes.build.execution.logs.guardrail.output.label'
        ) as string);
  const formattedGuardrailType = formatGuardrailType(config.guardrailType);

  // Determine default expanded state: expand if failed, collapse if success (unless message contains masking info)
  const hasMaskingInfo =
    config.message?.toLowerCase().includes('masked') ?? false;
  const defaultExpanded = !isSuccess || hasMaskingInfo;
  const nestedAccordionRemountKey =
    nestedAccordionSyncKey !== undefined
      ? `guardrail-nested-${nestedAccordionSyncKey}`
      : undefined;
  let nestedAccordionDefaultKeys: string[] = [];
  if (nestedAccordionSyncKey !== undefined) {
    nestedAccordionDefaultKeys = nestedAccordionSyncExpanded ? ['details'] : [];
  } else {
    nestedAccordionDefaultKeys = defaultExpanded ? ['details'] : [];
  }

  const title = (
    <span className='text-sm font-semibold text-on-surface'>{cardTitle}</span>
  );

  return (
    <LogCard
      title={title}
      textValue={cardTitle}
      footer={annotationSlot}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      itemKey={itemKey}
      itemClasses={itemClasses}
      className={className}
      defaultExpanded={defaultExpanded}
    >
      <div className='flex items-center justify-between'>
        <span className='text-xs font-medium text-on-surface-variant'>
          {formattedGuardrailType}
        </span>
        <Chip
          classNames={{
            base: isSuccess ? 'bg-positive-container' : 'bg-negative-container',
            content: `text-xs font-semibold ${
              isSuccess
                ? 'text-on-positive-container'
                : 'text-on-negative-container'
            }`
          }}
          radius='sm'
          size='sm'
        >
          {isSuccess
            ? (i18n.t('scenes.build.execution.logs.guardrail.passed') as string)
            : (i18n.t(
                'scenes.build.execution.logs.guardrail.failed'
              ) as string)}
        </Chip>
      </div>

      {/* Expandable Details section */}
      {hasDetails && (
        <div className='mt-3'>
          <Accordion
            key={nestedAccordionRemountKey}
            defaultSelectedKeys={nestedAccordionDefaultKeys}
            itemClasses={{
              ...nestedAccordionItemClasses,
              content: `mt-2 rounded-md p-3 ${detailsBgColor} ${detailsTextColor}`
            }}
          >
            <AccordionItem
              key='details'
              title={
                <span className='text-xs font-medium text-on-surface'>
                  {
                    i18n.t(
                      'scenes.build.execution.logs.guardrail.details'
                    ) as string
                  }
                </span>
              }
              textValue={
                i18n.t(
                  'scenes.build.execution.logs.guardrail.details'
                ) as string
              }
            >
              <p className='text-sm'>{config.message}</p>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </LogCard>
  );
}
